import React from 'react';
import { COLORS } from '../../../core/constants/colors';
import { FileUpload } from '../../dashboard_user/components/FileUpload';
import { EnrichmentForm, EnrichmentField, EnrichmentSession } from '../types';
import { Company } from '../../dashboard_user/types';

const COMPLETION_THRESHOLD = 80;

interface FormStepProps {
  session: EnrichmentSession | null;
  formData: EnrichmentForm | null;
  fieldResponses: Record<string, string>;
  additionalDocumentIds: number[];
  company: Company | null;
  submittingResponses: boolean;
  sessionError: string | null;
  responseMessage: string | null;
  onBack: () => void;
  onFieldChange: (field: EnrichmentField, value: string) => void;
  onAdditionalUploadComplete: (documentId: number) => void;
  onSubmitResponses: () => void;
  onSkipEnrichment: () => void;
}

const renderStatusBadge = (status?: string) => {
  if (!status) return null;
  const normalized = status.toLowerCase();
  let color = COLORS.extended.orange;
  if (normalized.includes('complete')) color = COLORS.primary.softLime;
  if (normalized.includes('missing')) color = COLORS.extended.redSalsa;
  if (normalized.includes('partial')) color = COLORS.extended.orange;

  return (
    <span
      className="text-xs font-semibold px-3 py-1 rounded-full"
      style={{ backgroundColor: color + '20', color }}
    >
      {status}
    </span>
  );
};

const renderFieldInput = (
  field: EnrichmentField,
  value: string,
  onFieldChange: (field: EnrichmentField, value: string) => void
) => {
  const commonProps = {
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onFieldChange(field, e.target.value),
    disabled: field.locked,
    className: 'w-full rounded-lg border-2 px-4 py-3 focus:outline-none focus:ring-2 transition-all text-sm',
    style: {
      borderColor: field.locked ? '#E5E7EB' : 'transparent',
      backgroundColor: field.locked ? '#F9FAFB' : '#F2F2F2',
      color: COLORS.primary.persianIndigo,
    },
    onFocus: (e: any) => {
      if (!field.locked) {
        e.target.style.borderColor = COLORS.extended.azure;
      }
    },
    onBlur: (e: any) => {
      if (!field.locked) {
        e.target.style.borderColor = 'transparent';
      }
    },
  };

  if (field.options && field.options.length > 0) {
    return (
      <select {...commonProps}>
        <option value="">Select an option</option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (field.field_type === 'text' || field.field_type === 'string') {
    return <textarea {...commonProps} rows={4} placeholder="Enter details..." />;
  }

  if (field.field_type === 'number') {
    return <input type="number" {...commonProps} />;
  }

  return <textarea {...commonProps} rows={3} placeholder="Enter response..." />;
};

export const FormStep: React.FC<FormStepProps> = ({
  session,
  formData,
  fieldResponses,
  additionalDocumentIds,
  company,
  submittingResponses,
  sessionError,
  responseMessage,
  onBack,
  onFieldChange,
  onAdditionalUploadComplete,
  onSubmitResponses,
  onSkipEnrichment,
}) => {
  return (
    <div className="flex-1 space-y-6">
      <div className="flex justify-end">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-semibold hover:underline"
          style={{ color: COLORS.primary.persianIndigo }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>
      {session && (
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                Current completeness
              </p>
              <div className="flex items-end gap-3">
                <p className="text-4xl font-bold" style={{ color: COLORS.extended.azure }}>
                  {Math.round(session.current_score)}%
                </p>
                <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                  Target: {COMPLETION_THRESHOLD}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={onSkipEnrichment}
                className="px-4 py-2 rounded-lg border-2 font-semibold transition-all"
                style={{ borderColor: '#E5E7EB', color: COLORS.primary.persianIndigo }}
              >
                Skip with current score
              </button>
            </div>
          </div>
          <div className="mt-6 w-full bg-gray-100 h-2 rounded-full">
            <div
              className="h-2 rounded-full transition-all"
              style={{ width: `${session.progress_percent}%`, backgroundColor: COLORS.extended.azure }}
            />
          </div>
        </div>
      )}

      {formData && (
        <div className="space-y-6">
          {formData.sections.map((section) => (
            <div key={section.section_id} className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.primary.persianIndigo }}>
                  {section.title}
                </h3>
                {section.description && (
                  <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                    {section.description}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                {section.fields.map((field) => (
                  <div key={field.field_id} className="rounded-lg border-2 p-5 space-y-3" style={{ borderColor: '#E5E7EB' }}>
                    <div className="flex flex-wrap items-center gap-3 justify-between">
                      <div>
                        <p className="text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                          {field.label}
                        </p>
                        {field.description && (
                          <p className="text-xs mt-1" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                            {field.description}
                          </p>
                        )}
                      </div>
                      {renderStatusBadge(field.status)}
                    </div>
                    {field.gaps && (
                      <div className="rounded-lg px-4 py-2 text-xs font-semibold" style={{ backgroundColor: COLORS.extended.redSalsa + '10', color: COLORS.extended.redSalsa }}>
                        Gaps detected: {field.gaps}
                      </div>
                    )}
                    {field.current_value && (
                      <div className="rounded-lg px-4 py-2 text-xs" style={{ backgroundColor: '#F9FAFB' }}>
                        <span className="font-semibold mr-2">Current data:</span>
                        {field.current_value}
                      </div>
                    )}
                    <div>{renderFieldInput(field, fieldResponses[field.field_id] ?? '', onFieldChange)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
        <div>
          <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.primary.persianIndigo }}>
            Additional supporting documents
          </h3>
          <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
            Upload evidence or supplemental files to improve your score.
          </p>
        </div>
        {company?.id && (
          <FileUpload
            companyId={company.id}
            onUploadComplete={onAdditionalUploadComplete}
            acceptedTypes={['.pdf', '.docx', '.xlsx', '.pptx', '.txt']}
          />
        )}
        {additionalDocumentIds.length > 0 && (
          <div className="rounded-lg border-2 px-4 py-3" style={{ borderColor: '#E5E7EB' }}>
            <p className="text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
              {additionalDocumentIds.length} additional document(s) attached
            </p>
          </div>
        )}
      </div>

      {sessionError && (
        <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}>
          <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>
            {sessionError}
          </p>
        </div>
      )}

      {responseMessage && (
        <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: COLORS.primary.softLime + '20' }}>
          <p className="text-sm font-semibold" style={{ color: COLORS.primary.softLime }}>
            {responseMessage}
          </p>
        </div>
      )}

      <div className="flex gap-4">
        <button
          onClick={onSubmitResponses}
          disabled={submittingResponses}
          className="flex-1 py-3 rounded-lg font-semibold text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: COLORS.extended.azure }}
        >
          {submittingResponses ? 'Submitting...' : 'Submit responses'}
        </button>
      </div>
    </div>
  );
};

